import { Navigate } from 'react-router-dom';

// project imports
import MainLayout from 'layout/MainLayout';
import Loadable from 'ui-component/Loadable';
import Cookies from 'universal-cookie';
import {
  fetchCustomizeLayoutPreferences,
  getLayoutPrefsSessionCachedPatch,
  setLayoutPrefsSessionCachedPatch
} from 'utils/layoutPreferencesApi';
import AuthGuard from 'utils/route-guard/AuthGuard';
import RoleFeatureGate from 'utils/route-guard/RoleFeatureGate';
import { MANAGER_ROLES, READ_ONLY_MANAGER_ROLES } from 'utils/managerRoles';
import { FEATURE_IDS } from 'utils/supportedFeatures';
import CloudRestApiGate from 'views/pages/administration/CloudRestApiGate';
import { AddDrawerProvider } from 'contexts/AddDrawerContext';
import DeviceGroupLevelDevice from '../views/pages/devicePage/deviceGroupLevelDevice';

import AuthPortalRedirect from '@/components/AuthPortalRedirect';
import OrgLocationGuard from '../utils/route-guard/OrgLocationGuard';
import Reports from '../views/pages/reports';
import AidnRouteGuard from '../views/aidn/AidnRouteGuard';
import HindsightRouteGuard from '../views/pages/hindsight/HindsightRouteGuard';

// sample page routing
Loadable(() => import('views/sample-page'));
const Dashboard = Loadable(() => import('views/pages/dashboard'));

// schema-driven switch config (Full-Configurability)
const FeatureConfigRouter = Loadable(() => import('views/device-config/FeatureConfigRouter'));

// stacking page routing
const StackDetail = Loadable(() => import('views/pages/stacking/StackDetail'));
Loadable(() => import('views/pages/organizations'));
const CreateOrganization = Loadable(() => import('views/pages/organizations/createOrganization'));
const WiFi = Loadable(() => import('views/pages/wi-fi'));
const SSID = Loadable(() => import('views/pages/wi-fi/SSID'));
const ManagementVLAN = Loadable(() => import('views/pages/wi-fi/managementVlan'));
const WiFiAdvanceWireless = Loadable(() => import('views/pages/wi-fi/advance/WirelessSettings'));
const WiFiAdvanceNetworkSettings = Loadable(() => import('views/pages/wi-fi/NetworkSettings'));
const WiFiAdvanceFastRoaming = Loadable(() => import('views/pages/wi-fi/advance/FastRoaming'));
const WiFiAdvanceURLFiltering = Loadable(() => import('views/pages/wi-fi/advance/URLFiltering'));
const WiFiAdvanceMeshNetworkSettings = Loadable(() => import('views/pages/wi-fi/advance/MeshNetworkSettings'));
const InstantWiFi = Loadable(() => import('views/pages/wi-fi/instantWiFi'));
// Device groups keep the Instant WiFi page as it was before the location redesign.
const DeviceGroupInstantWiFi = Loadable(() => import('views/pages/wi-fi/instantWiFi/DeviceGroupInstantWiFi'));
const MPSKSettings = Loadable(() => import('views/pages/wi-fi/advance/multiPSKSettings'));
const LoadBalancing = Loadable(() => import('views/pages/wi-fi/LoadBalancing'));
const Schedules = Loadable(() => import('views/pages/wi-fi/schedules'));
const Wired = Loadable(() => import('views/pages/wired'));
const WiredManagementVlan = Loadable(() => import('views/pages/wired/ManagementVlan'));

// location settings
const LocationSettings = Loadable(() => import('views/pages/locationSettings'));
const ConfigBackup = Loadable(() => import('views/pages/locationSettings/ConfigBackup'));
const LEDSettings = Loadable(() => import('views/pages/locationSettings/LEDSettings'));
const VLANSettings = Loadable(() => import('views/pages/locationSettings/VLAN'));

const Clients = Loadable(() => import('views/pages/clients'));
const SecurityCenter = Loadable(() => import('views/pages/securityCenter'));
const SchedulesPage = Loadable(() => import('views/pages/schedules/index.jsx'));
const DynamicDNS = Loadable(() => import('views/pages/dynamicDns'));
const Administration = Loadable(() => import('views/pages/administration'));
const UsersTab = Loadable(() => import('views/pages/administration/tabs/UsersTab'));
const CloudRestApiTab = Loadable(() => import('views/pages/administration/tabs/CloudRestApiTab'));
const PlanAndBilling = Loadable(() => import('views/pages/planAndBilling'));
const NetgearPartnerSubscriptionManagement = Loadable(() => import('views/pages/planAndBilling/NetgearPartnerSubscriptionManagement'));
const Events = Loadable(() => import('views/pages/events'));
Loadable(() => import('views/pages/mspMenu'));
const DeviceGroups = Loadable(() => import('views/pages/deviceGroupPage'));
const Device = Loadable(() => import('views/pages/devicePage'));
const DeviceAccessPoint = Loadable(() => import('views/pages/devicePage/deviceAccessPoint'));
const DeviceMobileHotspot = Loadable(() => import('views/pages/devicePage/deviceMobileHotspot'));
const DeviceOrbi = Loadable(() => import('views/pages/deviceDetail/orbi'));
const LocationDevice = Loadable(() => import('views/pages/devicePage/locationLevelDevice'));
const Firmware = Loadable(() => import('views/pages/firmware'));
const MspDashboardTab = Loadable(() => import('views/pages/mspMenu/mspDashboardTab'));
const MspOrganizationsTab = Loadable(() => import('views/pages/mspMenu/mspOrganizationsTab'));
const MspLocationsTab = Loadable(() => import('views/pages/mspMenu/mspLocationsTab'));
const MspDevicesTab = Loadable(() => import('views/pages/mspMenu/mspDevicesTab'));
// particuler Organization tabs
Loadable(() => import('views/pages/mspMenu/mspAdministrationTab'));
const NotificationsPage = Loadable(() => import('views/pages/notifications'));
const SupportRequestPage = Loadable(() => import('views/pages/supportRequest'));
Loadable(() => import('views/pages/organizations/orgDashboardTab'));
const OrgDataGridTab = Loadable(() => import('views/pages/organizations/orgDataGridTab'));
// Organization Administration subtabs
const OrgAdministration = Loadable(() => import('views/pages/organizations/orgAdministrationTab'));
const OrgUsersTab = Loadable(() => import('views/pages/organizations/administration/OrgUsersTab'));
const OrgPolicyTab = Loadable(() => import('views/pages/organizations/administration/OrgPolicyTab'));
const OrgScheduledRebootTab = Loadable(() => import('views/pages/organizations/administration/OrgScheduledRebootTab'));
const VLANSetup = Loadable(() => import('views/pages/wired/VLAN/VLANList'));
const TopologyTab = Loadable(() => import('views/pages/organizations/topologyTab'));
const RoutingSettings = Loadable(() => import('views/pages/locationSettings/Routing'));
const RadiusSettings = Loadable(() => import('views/pages/locationSettings/Radius'));
const GeneralSettings = Loadable(() => import('views/pages/locationSettings/General'));
const SnmpSettings = Loadable(() => import('views/pages/locationSettings/Snmp'));
const MDNSGateway = Loadable(() => import('views/pages/locationSettings/mDNSGateway'));
const SpanningTree = Loadable(() => import('views/pages/wired/SpanningTree'));
const RadiusConfiguration = Loadable(() => import('views/pages/wired/RadiusConfiguration'));
const Lag = Loadable(() => import('views/pages/wired/Lag'));
const PoESchedules = Loadable(() => import('views/pages/wired/poeSchedules/PoeDowntimeSchedules'));
const DhcpRelayConfiguration = Loadable(() => import('views/pages/wired/dhcpRelay/DhcpRelayConfiguration'));
const DhcpSnoopingConfiguration = Loadable(() => import('views/pages/wired/dhcpSnooping/DhcpSnoopingConfiguration'));
const DeviceSummary = Loadable(() => import('views/pages/deviceDetail'));
const SyslogConfiguration = Loadable(() => import('views/pages/locationSettings/SyslogConfiguration'));
const Profiles = Loadable(() => import('views/pages/wi-fi/manageProfiles/manageProfiles'));
const MobileHotspot = Loadable(() => import('views/pages/wi-fi/mobileHotspot'));
// importing Traffic Policies page
const DeviceGroupSettings = Loadable(() => import('views/pages/deviceGroupSettings'));
const TrafficPolicies = Loadable(() => import('views/pages/wi-fi/advance/TrafficPolicies'));
Loadable(() => import('views/pages/deviceGroupDashboard'));
const NewLogin = Loadable(() => import('views/pages/newLogin/index.jsx'));
const HindsightPage = Loadable(() => import('views/pages/hindsight/HindsightPage'));
// AIDN (AI Defined Network) — beta feature gated by the AIDN featureConf flags.
const AidnLanding = Loadable(() => import('views/aidn'));
const AidnSavedTopologies = Loadable(() => import('views/aidn/SavedTopologiesView'));
const AidnCreateNew = Loadable(() => import('views/aidn/create'));
const AidnWizardPage = Loadable(() => import('views/aidn/create/WizardPage'));
const AidnTextEntry = Loadable(() => import('views/aidn/create/TextEntry'));
const AidnSpeechCapture = Loadable(() => import('views/aidn/create/SpeechCapture'));
const AidnEnvironmentPlan = Loadable(() => import('views/aidn/create/EnvironmentPlan'));
// URL-only admin editor for the schema-driven Designer questions (F0b) — no nav button.
const AidnQuestionAdmin = Loadable(() => import('views/aidn/admin/QuestionAdmin'));
const AidnTopologyView = Loadable(() => import('views/aidn/TopologyView'));

/**
 * Customize GET for the logged-in shell. React Router runs this before rendering the
 * `MainRoutes` element (AuthGuard → MainLayout and children).
 *
 * When `sessionStorage` already has a patch for this `_Id`, returns **synchronously** so the
 * router does not await a Promise and the shell is not blocked. On a cache miss, returns a
 * Promise that performs one GET and writes the session cache (cleared on logout).
 *
 * `shouldRevalidate: false` avoids re-running this loader on client-side revalidations so
 * navigations do not briefly block on a redundant loader pass.
 */
function mainLayoutLoader() {
  const cookies = new Cookies();
  const id = cookies.get('_Id');
  if (!id) {
    return { patch: null };
  }
  const cached = getLayoutPrefsSessionCachedPatch(id);
  if (cached !== undefined) {
    return { patch: cached };
  }
  return fetchCustomizeLayoutPreferences().then((patch) => {
    const currentId = new Cookies().get('_Id');
    // If the user changed mid-flight, do not poison the old user’s cache or apply a mismatched patch.
    if (currentId && currentId === id) {
      setLayoutPrefsSessionCachedPatch(id, patch);
      return { patch };
    }
    return { patch: null };
  });
}

// ==============================|| MAIN ROUTING ||============================== //

const MainRoutes = {
  path: '/',
  id: 'main',
  loader: mainLayoutLoader,
  shouldRevalidate: () => false,
  element: (
    <AuthGuard>
      <OrgLocationGuard>
        <MainLayout />
      </OrgLocationGuard>
    </AuthGuard>
  ),
  children: [
    {
      path: '/',
      element: <MspDashboardTab />
    },
    {
      path: '/user-profile',
      element: <AuthPortalRedirect type="account" />
    },
    {
      path: '/user-profile/edit',
      element: <AuthPortalRedirect type="account" />
    },
    {
      path: '/user-profile/login-settings',
      element: <AuthPortalRedirect type="account" />
    },
    {
      path: '/user-profile/change-email',
      element: <AuthPortalRedirect type="account" />
    },
    {
      path: '/user-profile/change-password',
      element: <AuthPortalRedirect type="account" />
    },
    {
      path: '/locationSettings',
      element: <LocationSettings />
    },
    {
      path: '/locationSettings/radius',
      element: <RadiusSettings />
    },
    {
      path: '/locationSettings/snmp',
      element: <SnmpSettings />
    },
    {
      path: '/locationSettings/mdnsGateway',
      element: <MDNSGateway />
    },
    {
      path: '/locationSettings/general',
      element: (
        <AddDrawerProvider id="generalSettings-addDrawerProvider">
          <GeneralSettings />
        </AddDrawerProvider>
      )
    },
    {
      path: '/locationSettings/configBackup',
      element: (
        <AddDrawerProvider id="configBackup-addDrawerProvider">
          <ConfigBackup />
        </AddDrawerProvider>
      )
    },
    {
      path: '/locationSettings/routing',
      element: (
        <AddDrawerProvider id="routing-addDrawerProvider">
          <RoutingSettings />
        </AddDrawerProvider>
      )
    },
    {
      path: '/locationSettings/LED-Settings',
      element: (
        <AddDrawerProvider id="led-settings-addDrawerProvider">
          <LEDSettings />
        </AddDrawerProvider>
      )
    },
    {
      path: '/locationSettings/Syslog',
      element: (
        <AddDrawerProvider id="syslog-configuration-addDrawerProvider">
          <SyslogConfiguration />
        </AddDrawerProvider>
      )
    },
    {
      path: '/locationSettings/VLAN',
      element: (
        <AddDrawerProvider id="vlan-locationSettings-addDrawerProvider">
          <VLANSettings />
        </AddDrawerProvider>
      )
    },

    {
      path: '/mspHome/dashboard',
      element: <MspDashboardTab />
    },
    {
      path: '/mspHome/organizations',
      element: <MspOrganizationsTab />
    },
    {
      path: '/mspHome/locations',
      element: <MspLocationsTab />
    },
    {
      path: '/mspHome/devices',
      element: <MspDevicesTab />
    },
    {
      path: '/mspHome/administration',
      element: <Administration />
    },
    {
      path: '/mspHome/administration/users',
      element: (
        <RoleFeatureGate hiddenForRoles={MANAGER_ROLES}>
          <AddDrawerProvider>
            <UsersTab />
          </AddDrawerProvider>
        </RoleFeatureGate>
      )
    },
    {
      path: '/mspHome/administration/manage-subscriptions',
      element: (
        <AddDrawerProvider>
          <PlanAndBilling />
        </AddDrawerProvider>
      )
    },
    {
      path: '/mspHome/administration/reports',
      element: (
        <RoleFeatureGate hiddenForRoles={READ_ONLY_MANAGER_ROLES} featureId={FEATURE_IDS.REPORTS}>
          <AddDrawerProvider>
            <Reports />
          </AddDrawerProvider>
        </RoleFeatureGate>
      )
    },
    {
      path: '/mspHome/administration/lmsSubscriptions',
      element: (
        <AddDrawerProvider>
          <NetgearPartnerSubscriptionManagement />
        </AddDrawerProvider>
      )
    },
    {
      path: '/mspHome/administration/cloud-rest-api',
      element: (
        <AddDrawerProvider>
          <CloudRestApiGate>
            <CloudRestApiTab />
          </CloudRestApiGate>
        </AddDrawerProvider>
      )
    },
    {
      // Particular Organizations starts here
      path: '/organization/:orgName/dashboard',
      element: <Dashboard />
    },
    {
      path: '/organization/:orgName/dashboard/map',
      element: <Dashboard />
    },
    {
      path: '/organization/:orgName/locations',
      element: <OrgDataGridTab />
    },
    {
      path: '/organization/:orgName/device-Groups',
      element: 'device groups'
    },
    {
      path: '/organization/:orgName/devices',
      element: <MspDevicesTab callingFrom="parOrg" />
    },
    {
      path: '/organization/:orgName/clients',
      element: (
        <AddDrawerProvider id="clients-addDrawerProvider">
          <Clients calledFrom="orgLevel" />
        </AddDrawerProvider>
      )
    },
    {
      path: '/organization/:orgName/SSID',
      element: (
        <AddDrawerProvider id="ssid-addDrawerProviderLocationLevel">
          <SSID calledFrom="parOrg" />
        </AddDrawerProvider>
      )
    },
    {
      path: '/organization/:orgName/administration',
      element: <OrgAdministration />
    },
    {
      path: '/organization/:orgName/administration/users',
      element: (
        <RoleFeatureGate hiddenForRoles={MANAGER_ROLES}>
          <AddDrawerProvider>
            <OrgUsersTab />
          </AddDrawerProvider>
        </RoleFeatureGate>
      )
    },
    {
      path: '/organization/:orgName/administration/reports',
      element: (
        <RoleFeatureGate hiddenForRoles={READ_ONLY_MANAGER_ROLES} featureId={FEATURE_IDS.REPORTS}>
          <AddDrawerProvider>
            <Reports />
          </AddDrawerProvider>
        </RoleFeatureGate>
      )
    },
    {
      path: '/organization/:orgName/administration/policy',
      element: (
        <AddDrawerProvider>
          <OrgPolicyTab />
        </AddDrawerProvider>
      )
    },
    {
      path: '/organization/:orgName/administration/scheduled-reboot',
      element: (
        <AddDrawerProvider>
          <OrgScheduledRebootTab />
        </AddDrawerProvider>
      )
    },
    {
      path: 'organizations/createOrganization',
      element: <CreateOrganization />
    },
    {
      path: '/dashboard',
      element: <Dashboard />
    },
    {
      path: '/dashboard/topology',
      element: <Dashboard />
    },
    {
      path: '/dashboard/map',
      element: <Dashboard />
    },
    {
      path: '/dashboard/floor-map',
      element: <Dashboard />
    },
    {
      path: '/dashboard/floor-map/:floorId',
      element: <Dashboard />
    },
    {
      path: '/Wi-Fi',
      element: <WiFi />
    },
    { path: '/Wi-Fi/Advance/Network-Settings', element: <WiFiAdvanceNetworkSettings /> },
    {
      path: '/Wi-Fi/SSID',
      element: (
        <AddDrawerProvider id="ssid-addDrawerProviderLocationLevel">
          <SSID calledFrom="locationLevel" />
        </AddDrawerProvider>
      )
    },
    {
      path: '/Wi-Fi/ManagementVLAN',
      element: (
        <AddDrawerProvider>
          <ManagementVLAN calledFrom="locationLevel" />
        </AddDrawerProvider>
      )
    },
    {
      path: '/device-group/wireless/ManagementVLAN',
      element: (
        <AddDrawerProvider>
          <ManagementVLAN calledFrom="deviceGroup" />
        </AddDrawerProvider>
      )
    },
    {
      path: '/Wi-Fi/instantWiFi',
      element: <InstantWiFi />
    },
    {
      path: '/Wi-Fi/schedules',
      element: (
        <AddDrawerProvider id="schedules-addDrawerProvider">
          <Schedules />
        </AddDrawerProvider>
      )
    },
    {
      path: '/Wi-Fi/LoadBalancing',
      element: <LoadBalancing />
    },
    {
      path: '/device-group/wireless/LoadBalancing',
      element: <LoadBalancing />
    },
    {
      path: '/Wi-Fi/Profiles',
      element: <Profiles />
    },
    {
      path: '/MobileHotspot',
      element: <Navigate to="/MobileProfile/mobile-profiles" replace />
    },
    {
      path: '/MobileProfile',
      element: (
        <AddDrawerProvider id="mobileProfiles-addDrawerProvider">
          <MobileHotspot />
        </AddDrawerProvider>
      )
    },
    {
      path: '/MobileProfile/mobile-profiles',
      element: (
        <AddDrawerProvider id="mobileProfiles-addDrawerProvider">
          <MobileHotspot />
        </AddDrawerProvider>
      )
    },
    {
      path: '/MobileProfile/geofence',
      element: (
        <AddDrawerProvider id="mobileProfiles-addDrawerProvider">
          <MobileHotspot />
        </AddDrawerProvider>
      )
    },
    { path: '/Wi-Fi/Advance/WirelessSettings', element: <WiFiAdvanceWireless /> },
    { path: '/Wi-Fi/Advance/FastRoaming', element: <WiFiAdvanceFastRoaming /> },
    { path: '/Wi-Fi/Advance/URLFiltering', element: <WiFiAdvanceURLFiltering /> },
    { path: '/Wi-Fi/Advance/MeshNetworkSettings', element: <WiFiAdvanceMeshNetworkSettings /> },
    {
      path: '/Wi-Fi/Advance/MultiPSKSettings',
      element: (
        <AddDrawerProvider id="multiPSKSettings-addDrawerProvider">
          <MPSKSettings />
        </AddDrawerProvider>
      )
    },
    {
      path: '/Wi-Fi/Advance/TrafficPolicies',
      element: (
        <AddDrawerProvider id="advance-trafficpolicies">
          <TrafficPolicies />
        </AddDrawerProvider>
      )
    },
    {
      path: '/wired',
      element: <Wired />
    },
    {
      path: '/wired/PoESchedules',
      element: <PoESchedules />
    },
    {
      path: '/wired/VLAN',
      element: (
        <AddDrawerProvider id="vlan-addDrawerProvider">
          <VLANSetup />
        </AddDrawerProvider>
      )
    },
    // ---- AIDN (AI Defined Network) routes — additive, network-scoped, feature-gated ----
    // Every element is wrapped in <AidnRouteGuard> so a disabled user hitting a direct /aidn/*
    // URL gets the AIDN splash (defense-in-depth beyond the hidden nav tab).
    {
      path: '/aidn',
      element: (
        <AidnRouteGuard>
          <AidnLanding />
        </AidnRouteGuard>
      )
    },
    {
      path: '/aidn/saved',
      element: (
        <AidnRouteGuard>
          <AidnSavedTopologies />
        </AidnRouteGuard>
      )
    },
    {
      path: '/aidn/create',
      element: (
        <AidnRouteGuard>
          <AidnCreateNew />
        </AidnRouteGuard>
      )
    },
    {
      path: '/aidn/create/wizard',
      element: (
        <AidnRouteGuard>
          <AidnWizardPage />
        </AidnRouteGuard>
      )
    },
    {
      path: '/aidn/create/text',
      element: (
        <AidnRouteGuard>
          <AidnTextEntry />
        </AidnRouteGuard>
      )
    },
    {
      path: '/aidn/create/speech',
      element: (
        <AidnRouteGuard>
          <AidnSpeechCapture />
        </AidnRouteGuard>
      )
    },
    {
      path: '/aidn/create/environment-plan',
      element: (
        <AidnRouteGuard>
          <AidnEnvironmentPlan />
        </AidnRouteGuard>
      )
    },
    {
      path: '/aidn/question-update',
      element: (
        <AidnRouteGuard>
          <AidnQuestionAdmin />
        </AidnRouteGuard>
      )
    },
    {
      path: '/aidn/topology/:topologyId',
      element: (
        <AidnRouteGuard>
          <AidnTopologyView />
        </AidnRouteGuard>
      )
    },
    {
      path: '/clients',
      element: (
        <AddDrawerProvider id="clients-addDrawerProvider">
          <Clients calledFrom="locationLevel" />
        </AddDrawerProvider>
      )
    },
    {
      path: '/locationSettings/firmware',
      element: <Firmware />
    },
    {
      path: '/locationSettings/firmware/:tabId',
      element: <Firmware />
    },
    // {
    //   path: '/security',
    //   element: <Security />
    // },
    {
      path: '/security-center',
      element: <SecurityCenter />
    },
    {
      path: '/dynamicDns',
      element: <DynamicDNS />
    },
    {
      path: '/organization/:orgName/security-center',
      element: <SecurityCenter />
    },
    {
      path: '/administration',
      element: <Navigate to="/dashboard" replace /> // Redirect to dashboard if trying to access administration from Location level
    },
    {
      path: '/events',
      element: <Events />
    },
    {
      path: '/devices',
      element: (
        <AddDrawerProvider id="devices-addDrawerProvider">
          <Device callingFrom="locationLevel" />
        </AddDrawerProvider>
      )
    },
    {
      path: '/locationSettings/device-Groups',
      element: (
        <AddDrawerProvider id="deviceGroups-addDrawerProvider">
          <DeviceGroups callingFrom="locationLevel" />
        </AddDrawerProvider>
      )
    },
    {
      path: '/locationDevice',
      element: <LocationDevice />
    },
    {
      path: '/locationDevice/stack/:stackId',
      element: <StackDetail />
    },
    {
      path: '/locationDevice/stack/:stackId/:tab',
      element: <StackDetail />
    },
    {
      path: '/locationDevice/orbi/*',
      element: (
        <AddDrawerProvider id="orbi-addDrawerProvider">
          <DeviceOrbi />
        </AddDrawerProvider>
      )
    },
    {
      path: '/mspHome/topology', // this route is for location level topology
      element: <TopologyTab />
    },
    {
      path: '/devices/AP/summary',
      element: <DeviceAccessPoint />
    },
    {
      path: '/devices/AP/mesh',
      element: <DeviceAccessPoint />
    },
    {
      path: '/devices/AP/clients',
      element: (
        <AddDrawerProvider id="accessPoint-client-addDrawerProvider">
          <DeviceAccessPoint />
        </AddDrawerProvider>
      )
    },
    {
      path: '/devices/AP/configuration',
      element: <DeviceAccessPoint />
    },
    {
      path: '/devices/AP/neighboring-ap',
      element: <DeviceAccessPoint />
    },
    {
      path: '/devices/AP/troubleshoot',
      element: <DeviceAccessPoint />
    },
    {
      path: '/devices/AP/alarms',
      element: <DeviceAccessPoint />
    },
    {
      path: '/devices/MHS/summary',
      element: <DeviceMobileHotspot />
    },
    {
      path: '/devices/MHS/connected-clients',
      element: <DeviceMobileHotspot />
    },
    {
      path: '/devices/MHS/clients',
      element: <DeviceMobileHotspot />
    },
    {
      path: '/devices/MHS/ip-settings',
      element: <DeviceMobileHotspot />
    },
    {
      path: '/devices/MHS/settings',
      element: <DeviceMobileHotspot />
    },
    {
      path: '/devices/MHS/restore-and-backup',
      element: <DeviceMobileHotspot />
    },
    {
      path: '/devices/MHS/troubleshoot',
      element: <DeviceMobileHotspot />
    },
    {
      path: '/devices/MHS/alarms',
      element: <DeviceMobileHotspot />
    },
    {
      path: '/notifications',
      element: <NotificationsPage />
    },
    {
      path: '/scheduled',
      element: (
        <AddDrawerProvider id="scheduled-hub-loc">
          <SchedulesPage />
        </AddDrawerProvider>
      )
    },
    {
      path: '/mspHome/notifications',
      element: <NotificationsPage />
    },
    {
      path: '/organization/:orgName/notifications',
      element: <NotificationsPage />
    },
    {
      path: '/organization/:orgName/scheduled',
      element: (
        <AddDrawerProvider id="scheduled-hub-org">
          <SchedulesPage />
        </AddDrawerProvider>
      )
    },
    {
      path: '/support/usersList',
      element: <SupportRequestPage />
    },
    {
      path: '/wired/spanningTree',
      element: <SpanningTree />
    },
    {
      path: '/wired/managementVlan',
      element: (
        <AddDrawerProvider id="wired-management-vlan-addDrawerProvider">
          <WiredManagementVlan />
        </AddDrawerProvider>
      )
    },
    {
      path: '/wired/radiusConfiguration',
      element: (
        <AddDrawerProvider id="radiusConfiguration-addDrawerProvider">
          <RadiusConfiguration />
        </AddDrawerProvider>
      )
    },
    {
      path: '/wired/lag',
      element: <Lag />
    },
    {
      path: '/wired/dhcpRelay',
      element: <DhcpRelayConfiguration />
    },
    {
      path: '/wired/dhcpSnooping',
      element: <DhcpSnoopingConfiguration />
    },
    {
      path: '/device-group/devices',
      element: <DeviceGroupLevelDevice />
    },
    {
      path: '/devices/:deviceType/:tab',
      element: <DeviceSummary />
    },
    {
      path: '/device/:serialNo/config/:featurePath',
      element: <FeatureConfigRouter />
    },
    {
      path: '/device-group/wired/vlan',
      element: (
        <AddDrawerProvider id="vlan-addDrawerProvider">
          <VLANSetup />
        </AddDrawerProvider>
      )
    },
    {
      path: '/device-group/settings',
      element: (
        <AddDrawerProvider id="deviceGroupSettings-addDrawerProvider">
          <DeviceGroupSettings />
        </AddDrawerProvider>
      )
    },
    {
      path: '/device-group/wireless/SSID',
      element: (
        <AddDrawerProvider id="ssid-addDrawerProviderLocationLevel">
          <SSID calledFrom="deviceGroupLevel" />
        </AddDrawerProvider>
      )
    },
    {
      path: '/device-group/wireless/Advance/WirelessSettings',
      element: <WiFiAdvanceWireless />
    },
    {
      path: '/device-group/wireless/Advance/Network-Settings',
      element: <WiFiAdvanceNetworkSettings />
    },
    {
      path: '/device-group/wireless/Advance/FastRoaming',
      element: <WiFiAdvanceFastRoaming />
    },
    {
      path: '/device-group/wireless/Advance/MultiPSKSettings',
      element: (
        <AddDrawerProvider id="multiPSKSettings-customgroup-addDrawerProvider">
          <MPSKSettings />
        </AddDrawerProvider>
      )
    },
    {
      path: '/device-group/wireless/instantWiFi',
      element: <DeviceGroupInstantWiFi />
    },
    {
      path: '/device-group/wireless/schedules',
      element: (
        <AddDrawerProvider id="schedules-deviceGroup-addDrawerProvider">
          <Schedules />
        </AddDrawerProvider>
      )
    },
    {
      path: '/locationDevice/stack/:stackId/:tab?',
      element: <StackDetail />
    },
    {
      path: '/newLogin',
      element: <NewLogin />
    },
    {
      // Wrapped in <HindsightRouteGuard> so a disabled user hitting the direct /hindsight URL sees the
      // "unavailable" notice instead of the page (whose data calls would 404). Nav is gated separately.
      path: '/hindsight',
      element: (
        <HindsightRouteGuard>
          <HindsightPage />
        </HindsightRouteGuard>
      )
    }
  ]
};

export default MainRoutes;
